import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Row, Col, Skeleton, Badge, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTrendUp } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { AppDispatch, RootState } from '../../../redux/store';
import { fetchMonthlyReport } from '../../../redux/MonthlyReports';
import { useTheme } from '../../../context/ThemeContext';
import {
  SummaryCard,
  SummaryHeader,
  SummaryTitle,
  SummaryInfo,
  PeriodText,
  GrossSalesDisplay,
  GrossSalesAmount,
  GrossSalesLabel,
  MetricsGrid,
  MetricCard,
  MetricHeader,
  MetricIcon,
  MetricTitle,
  MetricValue,
  StyledSkeletonSmall,
  StyledSkeletonMedium,
  StyledSkeletonLarge,
  KeyMetricsTitle,
} from './Styles';
import {
  addCommasInPrices,
  METRIC_IDS,
  METRIC_COLORS,
  METRIC_ICONS,
} from './constants';
import { MonthlyReportSummaryProps, KeyMetric } from './types';

const { Text } = Typography;

const MonthlyReportSummary: React.FC<MonthlyReportSummaryProps> = ({
  merchant,
  storeId,
  selectedMonth,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { reportData, loading } = useSelector(
    (state: RootState) => state.monthlyReports
  );

  const getMonthTimestamp = (month: moment.Moment | Date | null): number | null => {
    if (!month) return null;
    if (month instanceof Date) return month.getTime();
    if (moment.isMoment(month)) return month.valueOf();
    return null;
  };

  const monthTimestamp = getMonthTimestamp(selectedMonth);
  const merchantId = merchant?.id || null;

  const monthStr = useMemo(() => {
    if (!monthTimestamp) return null;
    return moment(monthTimestamp).format('YYYY-MM');
  }, [monthTimestamp]);

  const memoizedMerchantId = useMemo(() => merchantId, [merchantId]);
  
  const paramsKey = useMemo(() => {
    if (!monthStr || !merchant?.id || !storeId) return null;
    return `${monthStr}-${memoizedMerchantId || ''}-${storeId}`;
  }, [monthStr, memoizedMerchantId, storeId, merchant?.id]);
  
  const previousParamsKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!paramsKey || loading) {
      return;
    }

    if (previousParamsKeyRef.current === paramsKey) {
      return;
    }

    previousParamsKeyRef.current = paramsKey;

    const params = {
      month: monthStr!,
      merchantId: memoizedMerchantId || undefined,
      storeId: storeId || undefined,
    };
    
    dispatch(fetchMonthlyReport(params));
  }, [dispatch, paramsKey, monthStr, memoizedMerchantId, storeId, loading]);

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };

  const salesSummary = reportData?.salesSummary || {
    grossSales: 0,
    netSales: 0,
    orderCount: 0,
    currency: 'USD',
  };

  const daysInMonth = useMemo(() => {
    if (!selectedMonth) return 30;
    const month = moment.isMoment(selectedMonth) ? selectedMonth : moment(selectedMonth);
    return month.daysInMonth();
  }, [selectedMonth]);

  const keyMetrics: KeyMetric[] = [
    {
      id: METRIC_IDS.GROSS_SALES,
      title: 'Gross Sales',
      subTitle: 'Total sales before deductions',
      value: `$${addCommasInPrices(salesSummary.grossSales || 0)}`,
      icon: METRIC_ICONS[METRIC_IDS.GROSS_SALES],
      color: METRIC_COLORS.GROSS_SALES,
    },
    {
      id: METRIC_IDS.NET_SALES,
      title: 'Net Sales',
      subTitle: 'Sales after deductions',
      value: `$${addCommasInPrices(salesSummary.netSales || 0)}`,
      icon: METRIC_ICONS[METRIC_IDS.NET_SALES],
      color: METRIC_COLORS.NET_SALES,
    },
    {
      id: METRIC_IDS.ORDER_COUNT,
      title: 'Total Orders',
      subTitle: 'Number of orders processed',
      value: (salesSummary.orderCount || 0).toString(),
      icon: METRIC_ICONS[METRIC_IDS.ORDER_COUNT],
      color: METRIC_COLORS.ORDER_COUNT,
    },
    {
      id: METRIC_IDS.AVERAGE_ORDER_VALUE,
      title: 'Avg Order Value',
      subTitle: 'Average value per order',
      value:
        salesSummary.orderCount > 0
          ? `$${addCommasInPrices(
              (salesSummary.grossSales || 0) / salesSummary.orderCount
            )}`
          : '$0.00',
      icon: METRIC_ICONS[METRIC_IDS.AVERAGE_ORDER_VALUE],
      color: METRIC_COLORS.AVERAGE_ORDER_VALUE,
    },
    {
      id: METRIC_IDS.DAILY_AVERAGE,
      title: 'Daily Average',
      subTitle: 'Average daily sales',
      value: `$${addCommasInPrices((salesSummary.grossSales || 0) / daysInMonth)}`,
      icon: METRIC_ICONS[METRIC_IDS.DAILY_AVERAGE],
      color: METRIC_COLORS.DAILY_AVERAGE,
    },
    {
      id: METRIC_IDS.CURRENCY,
      title: 'Currency',
      subTitle: 'Report currency',
      value: salesSummary.currency || 'USD',
      icon: METRIC_ICONS[METRIC_IDS.CURRENCY],
      color: METRIC_COLORS.CURRENCY,
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <SummaryCard>
        <SummaryHeader>
          <SummaryTitle>Monthly Report Summary</SummaryTitle>
          <SummaryInfo>
            {loading ? (
              <StyledSkeletonSmall active size="small" />
            ) : (
              <>
                <Text type="secondary">
                  Merchant: {reportData?.merchantName || 'N/A'}
                </Text>
                <Badge
                  color="blue"
                  text={reportData?.reportType || 'monthly'}
                />
              </>
            )}
          </SummaryInfo>
        </SummaryHeader>

        {loading ? (
          <StyledSkeletonMedium active />
        ) : (
          reportData?.period?.month && reportData?.period?.year && (
            <PeriodText>
              {reportData.period.month} {reportData.period.year} -{' '}
              {reportData.period.startDate && reportData.period.endDate && (
                <>
                  {moment(reportData.period.startDate).format('MMM DD')} -{' '}
                  {moment(reportData.period.endDate).format('MMM DD, YYYY')}
                </>
              )}
            </PeriodText>
          )
        )}

        <GrossSalesDisplay>
          {loading ? (
            <StyledSkeletonLarge active size="large" />
          ) : (
            <GrossSalesAmount>
              ${addCommasInPrices(salesSummary.grossSales || 0)}
            </GrossSalesAmount>
          )}
          <GrossSalesLabel>
            <FontAwesomeIcon icon={faArrowTrendUp} />
            <span>Gross Sales</span>
          </GrossSalesLabel>
        </GrossSalesDisplay>

        <div>
          {loading ? (
            <Row gutter={[16, 16]}>
              {Array.from({ length: 6 }).map((_, index) => (
                <Col xs={24} sm={12} md={8} lg={6} xl={4} key={index}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Col>
              ))}
            </Row>
          ) : (
            <>
              <KeyMetricsTitle level={4}>
                Key Metrics
              </KeyMetricsTitle>
              <MetricsGrid gutter={[16, 16]}>
                {keyMetrics.map((metric) => (
                  <Col xs={24} sm={12} md={8} lg={6} xl={4} key={metric.id}>
                    <MetricCard>
                      <MetricHeader>
                        <MetricIcon color={metric.color}>
                          <FontAwesomeIcon icon={metric.icon} />
                        </MetricIcon>
                        <MetricTitle>{metric.title}</MetricTitle>
                      </MetricHeader>
                      <MetricValue>{metric.value}</MetricValue>
                    </MetricCard>
                  </Col>
                ))}
              </MetricsGrid>
            </>
          )}
        </div>
      </SummaryCard>
    </ThemeProvider>
  );
};

export default MonthlyReportSummary;
